import { Add, Button, FormCard } from '@lmc/ui-kit'
import { Heading, Text } from '@lmc/ui-kit/src/general'
import { Alert } from 'antd'
import { showToast } from 'common/utils'
import useResidentNeeds from 'hooks/useResidentNeeds'
import { NAMESPACE } from 'locale'
import { carePlansConstants } from 'locale/carePlans'
import type React from 'react'
import { Suspense, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import Field from '../../../../../../components/fields'
import type {
  PopulatedResidentNeed,
  ResidentNeedSnapshot
} from '../../../../hooks/useCarePlanPopulated'
import AddNeedsModal from './AddNeedsModal'
import NeedsList from './NeedsList'
import RemoveNeedModal from './RemoveNeedModal'

interface NeedsSectionProps {
  residentId: string
  residentNeeds?: PopulatedResidentNeed[]
  residentNeedsSnapshot?: ResidentNeedSnapshot[]
  value?: string[]
  disabled?: boolean
  onChange?: (needIds: string[]) => void
  isRevision?: boolean
  lastReviewDate?: string
  notesValue?: string | null
  formId?: string | null
  fields?: object
  skillNeedsContent?: React.ReactNode
}

const NeedsSection: React.FC<NeedsSectionProps> = ({
  residentId,
  residentNeeds = [],
  residentNeedsSnapshot = [],
  value,
  disabled = false,
  onChange,
  isRevision = false,
  lastReviewDate,
  notesValue,
  formId,
  fields,
  skillNeedsContent
}) => {
  const { t } = useTranslation(NAMESPACE.CARE_PLANS)
  const [isAddModalVisible, setIsAddModalVisible] = useState(false)
  const [removeNeedId, setRemoveNeedId] = useState<string | null>(null)

  // Needed to display newly-added needs not yet in the populated response
  const { needs: allResidentNeeds } = useResidentNeeds({
    id: !disabled && !isRevision ? residentId : ''
  })

  const effectiveIds = value ?? residentNeeds.map((n) => n.id)

  const displayedNeeds: PopulatedResidentNeed[] = isRevision
    ? residentNeedsSnapshot
    : disabled
      ? effectiveIds.length > 0
        ? residentNeeds.filter((n) => effectiveIds.includes(n.id))
        : residentNeeds
      : (effectiveIds
          .map((needId) => {
            const populated = residentNeeds.find((n) => n.id === needId)
            if (populated) return populated
            const resident = allResidentNeeds.find((n) => n.id === needId)
            if (resident) {
              return {
                id: resident.id,
                need: resident.need,
                notes: resident.notes,
                addedOn: resident.addedOn,
                addedBy: resident.addedBy,
                editedOn: resident.editedOn,
                editedBy: resident.editedBy
              }
            }
            return null
          })
          .filter(Boolean) as PopulatedResidentNeed[])

  const handleOpenAddModal = () => {
    setIsAddModalVisible(true)
  }

  const handleAddNeeds = (selectedIds: string[]) => {
    onChange?.([...effectiveIds, ...selectedIds])
    showToast({ title: t(carePlansConstants.NEEDS_ADDED_TOAST) })
  }

  const handleRemoveClick = (needId: string) => {
    setRemoveNeedId(needId)
  }

  const handleRemoveConfirm = () => {
    if (removeNeedId) {
      const updatedIds = effectiveIds.filter((id) => id !== removeNeedId)
      onChange?.(updatedIds)
      showToast({ title: t(carePlansConstants.NEED_REMOVED_TOAST) })
    }
    setRemoveNeedId(null)
  }

  const handleRemoveCancel = () => {
    setRemoveNeedId(null)
  }

  const hasNeeds = displayedNeeds.length > 0
  const hasNoSnapshotData = isRevision && residentNeedsSnapshot.length === 0

  return (
    <>
      <StyledFormCard
        title={
          <FormCardHeading level={3}>
            {t(carePlansConstants.NEEDS_SECTION_TITLE)}
          </FormCardHeading>
        }
        rightChildren={
          !disabled &&
          !isRevision && (
            <Button
              type="button"
              variant="text"
              icon={<Add />}
              onClick={handleOpenAddModal}
            >
              {t(carePlansConstants.ADD_NEEDS_BUTTON)}
            </Button>
          )
        }
      >
        {hasNoSnapshotData ? (
          <Alert
            message={t(carePlansConstants.NEEDS_NO_SNAPSHOT)}
            type="info"
            showIcon
          />
        ) : hasNeeds ? (
          <NeedsList
            items={displayedNeeds}
            disabled={disabled}
            lastReviewDate={lastReviewDate}
            onRemove={!disabled ? handleRemoveClick : undefined}
            testId="care-plan-needs-list"
          />
        ) : undefined}
        {skillNeedsContent}
        {(formId || notesValue) && (
          <NotesBlock>
            <NotesLabel weight="medium">Notes</NotesLabel>
            {formId ? (
              <TextareaWrapper>
                <Suspense fallback={<div>Loading...</div>}>
                  <Field
                    keys="needs"
                    // @ts-ignore
                    label="Needs"
                    layout="no-label"
                    formId={formId}
                    style={textareaStyle}
                    type="textarea"
                    values={fields}
                  />
                </Suspense>
              </TextareaWrapper>
            ) : (
              <NotesText>{notesValue}</NotesText>
            )}
          </NotesBlock>
        )}
      </StyledFormCard>
      {!disabled && !isRevision && (
        <AddNeedsModal
          isModalVisible={isAddModalVisible}
          setIsModalVisible={setIsAddModalVisible}
          residentId={residentId}
          linkedNeedIds={effectiveIds}
          onConfirm={handleAddNeeds}
        />
      )}
      <RemoveNeedModal
        isModalVisible={!!removeNeedId}
        setIsModalVisible={(visible) => {
          if (!visible) handleRemoveCancel()
        }}
        onConfirm={handleRemoveConfirm}
        onCancel={handleRemoveCancel}
      />
    </>
  )
}

const StyledFormCard = styled(FormCard)`
  .ant-divider {
    margin: 16px 0 !important;
  }
`

const FormCardHeading = styled(Heading)`
  color: ${({ theme }) => theme.color.neutral.color4};
`

const NotesBlock = styled.div`
  display: flex;
  flex-direction: column;
`

const NotesLabel = styled(Text)`
  font-size: 15px;
  color: ${({ theme }) => theme.color.neutral.color3};
  margin-bottom: 0;
`

const NotesText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color4};
  white-space: pre-wrap;
  margin-top: 4px;
`

const TextareaWrapper = styled.div`
  margin-top: 8px;

  > div {
    min-height: 0 !important;
    margin-top: 0 !important;
    padding: 0 !important;
  }

  > div > div {
    min-width: 0 !important;
    padding: 0 !important;
    margin: 0 !important;
  }

  textarea {
    margin-top: 0 !important;
  }
`

const textareaStyle = {
  minHeight: 100,
  padding: '12px 16px',
  resize: 'vertical' as const,
  borderRadius: 8,
  width: '100%'
}

export default NeedsSection
